import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {api} from 'nutshell-core/api';
import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import type {User} from 'nutshell-core/users/users-types';

import {useGetSetting} from '../../core/use-get-setting';
import {useCoreEntityScrollCheckpoint} from '../../core-entity/hooks/use-core-entity-scroll-checkpoint';
import {CoreEntityContentContainer} from '../../core-entity/layout/core-entity-content-container';
import {CoreEntityMainColumn} from '../../core-entity/layout/core-entity-main-column';
import {CoreEntityPageContainer} from '../../core-entity/layout/core-entity-page-container';
import {LoadingPage} from '../../core-entity/layout/loading-page';
import {TAB_ACTIVITY, TAB_NOTE} from '../../core-entity/redux/consts';
import {setActionBarTab} from '../../core-entity/redux/core-entity-page-reducer';

import {PeepRedesignOnboardingModal} from '../onboarding/peep-redesign-onboarding-modal';

import {ContactContent} from './content/contact-content';
import {useGetContact} from './graphql/hooks/use-get-contact';
import {useGetContactLastContactedTime} from './graphql/hooks/use-get-contact-last-contacted-time';
import {ContactHeader} from './header/contact-header';
import {useContactUpdateRefetch} from './hooks/use-contact-update-refetch';
import {useUpdateContactHandlers} from './hooks/use-update-contact-handlers';
import {ContactSidebar} from './sidebar/contact-sidebar';

interface Props {
    contactId: string;
    user: User;
}

export function ContactPage(props: Props) {
    const lastViewedContactId = useRef<string | null>(null);

    const {
        hasScrolledPastCheckpoint,
        scrollContainerTop,
        scrollContainerEl,
        setScrollContainerRef,
        setActionBarRef,
    } = useCoreEntityScrollCheckpoint();

    const {contact, isLoading: isContactLoading} = useGetContact(props.contactId);
    const {contactLastContact, isLoading: isLoadingContactLastContact} =
        useGetContactLastContactedTime(props.contactId);

    const {
        handleUpdateName,
        handleUpdateAvatar,
        handleUpdateAssignee,
        handleUpdateDeletedStatus,
        handleUpdateContactTerritory,
        handleUpdateDescription,
        handleHardDelete,
        handleUpdateTags,
    } = useUpdateContactHandlers(contact);

    const {setting: isSidebarCollapsed, isFirstLoad: isSidebarCollapsedLoading} = useGetSetting({
        settingName: Nut_Settings_User.SETTING_CONTACT_WORKSPACE_SIDEBAR_COLLAPSED,
    });
    const {setting: contactPageActionBarTab, isFirstLoad: isContactPageActionBarTabLoading} =
        useGetSetting({
            settingName: Nut_Settings_User.SETTING_CONTACT_PAGE_ACTION_BAR_TAB,
        });

    useContactUpdateRefetch(props.contactId);

    const dispatch = useDispatch();

    const sidebarCollapsedState = Boolean(
        isSidebarCollapsed && Number(isSidebarCollapsed.value) === 1
    );

    // When the tab loads via setting, update the redux state
    useEffect(() => {
        if (
            contactPageActionBarTab &&
            (contactPageActionBarTab.value === TAB_NOTE ||
                contactPageActionBarTab.value === TAB_ACTIVITY)
        ) {
            dispatch(setActionBarTab(contactPageActionBarTab.value));
        }
    }, [contactPageActionBarTab, dispatch]);

    useEffect(() => {
        if (contact && lastViewedContactId.current !== contact.id) {
            lastViewedContactId.current = contact.id;
            api.post(`views/${contact.id}`, {});
        }
    }, [contact]);

    if (
        !contact ||
        isSidebarCollapsedLoading ||
        isContactLoading ||
        isContactPageActionBarTabLoading
    ) {
        return <LoadingPage />;
    }

    const hasFullHeightSidebar = NutClientConfig.hasCoreEntityFullHeightSidebarFF;

    const headerEl = (
        <ContactHeader
            contact={contact}
            contactLastContact={contactLastContact}
            isLoadingContactLastContact={isLoadingContactLastContact}
            user={props.user}
            onUpdateContactName={handleUpdateName}
            onUpdateAvatar={handleUpdateAvatar}
            onUpdateAssignee={handleUpdateAssignee}
            onUpdateDeletedStatus={handleUpdateDeletedStatus}
            onUpdateContactTerritory={handleUpdateContactTerritory}
            onUpdateDescription={handleUpdateDescription}
            onHardDelete={handleHardDelete}
            onUpdateTags={handleUpdateTags}
        />
    );

    const contentEl = (
        <ContactContent
            contact={contact}
            scrollContainerRef={scrollContainerEl}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            getActionBarRef={setActionBarRef}
            isSidebarCollapsed={sidebarCollapsedState}
            scrollContainerTop={scrollContainerTop}
            onUpdateAssignee={handleUpdateAssignee}
            onUpdateDeletedStatus={handleUpdateDeletedStatus}
            onHardDelete={handleHardDelete}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    const sidebarEl = (
        <ContactSidebar
            contact={contact}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    return (
        <CoreEntityPageContainer
            title={`Person · ${contact.name}`}
            hasFullHeightSidebar={hasFullHeightSidebar}
            scrollContainerRef={hasFullHeightSidebar ? undefined : setScrollContainerRef}
        >
            {hasFullHeightSidebar ? (
                <>
                    <CoreEntityMainColumn scrollContainerRef={setScrollContainerRef}>
                        {headerEl}
                        {contentEl}
                    </CoreEntityMainColumn>
                    {sidebarEl}
                </>
            ) : (
                <>
                    {headerEl}
                    <CoreEntityContentContainer>
                        {contentEl}
                        {sidebarEl}
                    </CoreEntityContentContainer>
                </>
            )}
            <PeepRedesignOnboardingModal />
        </CoreEntityPageContainer>
    );
}
